/**
 * Login real do painel (modo ao vivo; `apps/server/src/panel.ts`): e-mail e senha conferidos no
 * servidor (scrypt), sessão num cookie HttpOnly assinado (`rota_sid`, 1 dia ou 30 com "Manter
 * conectado"). O navegador nunca vê o token: a sessão é lida em `GET /api/auth/session`.
 *
 *   POST /api/auth/login {email, password, remember} → 200 {user} | 401 | 429
 *   GET  /api/auth/session → 200 {user} | 401        POST /api/auth/logout
 *   POST /api/auth/password {current, next} → 200 | 400 {error} (troca a própria senha)
 *   POST /api/auth/invite/accept {token, password, name} → 200 {user} | 400 {error}
 *   Equipe (só o dono): /api/team, /api/team/invite(s), /api/team/member, /api/team/revoke, /api/team/audit
 *
 * O que o servidor ainda não tem (2FA, redefinir senha por e-mail) rejeita com
 * `source_unavailable`, que as telas já tratam. A confirmação de identidade (step-up) confere a
 * senha de novo no próprio `login` e vale 15 min, como no mock.
 */
import type {
  AuthProvider,
  Id,
  InviteResult,
  Page,
  Role,
  SessionInfo,
  SignInResult,
  SuiteAuditAction,
  SuiteAuditEntry,
  TeamMember,
} from '@rota/contracts';
import { createHttp, type Http, type HttpOptions } from '../api/http';
import { buildSession, defaultFunctionsFor } from './access';
import { initialsOf } from './seed';

/** Usuário como o servidor devolve (`publicUser`). */
export interface ServerUser {
  email: string;
  name: string;
  role: 'owner' | 'partner' | 'support';
}

const STEP_UP_MS = 15 * 60_000;
const DAY = 86_400_000;
/** O servidor bloqueia o IP com 8 erros em 15 min; o pior caso é esperar a janela inteira. */
const THROTTLE_MS = 15 * 60_000;
const ROLES: readonly Role[] = ['owner', 'partner', 'support'];

/** Login da equipe como o servidor devolve (`GET /api/team`). */
interface ServerMember extends ServerUser {
  status: 'active' | 'disabled';
  lastSeenAt: number | null;
}
interface ServerInvite {
  id: string;
  email: string;
  name: string;
  role: ServerUser['role'];
  createdAt: number;
  expiresAt: number;
  createdBy: string;
}
interface ServerAudit {
  id: number;
  at: number;
  type: string;
  detail: { email?: string; by?: string; role?: string; status?: string; disabled?: boolean };
}

/** Nome do nível de acesso no servidor (o painel só aplica estes três). */
export const LIVE_ROLE_NAME: Readonly<Record<ServerUser['role'], string>> = {
  owner: 'Dono',
  partner: 'Sócio',
  support: 'Atendimento',
};

const memberId = (email: string): Id => `u_${email}`;
const inviteId = (id: string): Id => `inv_${id}`;
const emailOf = (id: Id): string => (id.startsWith('u_') ? id.slice(2) : id);

function toMember(u: ServerMember): TeamMember {
  const role: Role = ROLES.includes(u.role) ? u.role : 'support';
  const name = u.name?.trim() || u.email;
  return {
    id: memberId(u.email),
    name,
    initials: initialsOf(name),
    email: u.email,
    role,
    functions: defaultFunctionsFor(role),
    status: u.status,
    lastSeenAt: u.lastSeenAt,
    mfaEnrolled: false,
  };
}

function inviteMember(i: ServerInvite): TeamMember {
  return {
    id: inviteId(i.id),
    name: i.name,
    initials: initialsOf(i.name),
    email: i.email,
    role: i.role,
    functions: defaultFunctionsFor(i.role),
    status: 'invited',
    lastSeenAt: null,
    mfaEnrolled: false,
  };
}

const AUDIT_ACTION: Record<string, SuiteAuditAction> = {
  panel_login: 'login_ok',
  panel_login_failed: 'login_failed',
  panel_logout: 'logout',
  panel_password_changed: 'password_change',
  panel_invite: 'invite',
  panel_invite_cancelled: 'invite',
  panel_invite_accepted: 'invite',
  panel_team_change: 'role_change',
  panel_sessions_revoked: 'session_revoked',
  panel_user_set: 'role_change',
};

/** Linha do histórico do servidor → texto curto em português. */
function auditDetail(r: ServerAudit): string {
  const d = r.detail;
  const role = d.role && d.role in LIVE_ROLE_NAME ? LIVE_ROLE_NAME[d.role as ServerUser['role']] : null;
  switch (r.type) {
    case 'panel_login_failed':
      return d.disabled ? `${d.email ?? ''} · acesso desativado` : `${d.email ?? ''} · senha errada`;
    case 'panel_invite':
      return `para ${d.email ?? ''}${role ? ` (${role})` : ''}`;
    case 'panel_invite_cancelled':
      return `cancelado: ${d.email ?? ''}`;
    case 'panel_invite_accepted':
      return `${d.email ?? ''} aceitou e entrou${role ? ` (${role})` : ''}`;
    case 'panel_team_change':
      return [
        d.email,
        role ? `nível: ${role}` : null,
        d.status === 'disabled' ? 'acesso desativado' : d.status === 'active' ? 'acesso reativado' : null,
      ]
        .filter(Boolean)
        .join(' · ');
    case 'panel_sessions_revoked':
      return `${d.email ?? ''} saiu de todos os aparelhos`;
    case 'panel_user_set':
      return `acesso criado para ${d.email ?? ''}${role ? ` (${role})` : ''}`;
    default:
      return '';
  }
}

const unavailable = (what: string) =>
  Promise.reject({ code: 'source_unavailable', message: `${what} ainda não existe no servidor do painel.` });

/** Usuário do servidor → `SessionInfo` (permissões calculadas como no mock, pelo papel). */
export function sessionFromUser(
  u: ServerUser,
  s: { remembered: boolean; stepUpUntil: number | null; now: number },
): SessionInfo {
  const role: Role = ROLES.includes(u.role) ? u.role : 'support';
  const name = u.name?.trim() || u.email.split('@')[0] || u.email;
  return buildSession(
    {
      id: `u_${u.email}`,
      name,
      initials: initialsOf(name),
      email: u.email,
      role,
      functions: defaultFunctionsFor(role),
      mfaEnrolled: false,
    },
    {
      expiresAt: s.now + (s.remembered ? 30 : 1) * DAY,
      stepUpUntil: s.stepUpUntil,
      remembered: s.remembered,
    },
  );
}

export interface ApiAuthOptions extends HttpOptions {
  now?: () => number;
}

export class ApiAuthProvider implements AuthProvider {
  readonly kind = 'api' as const;
  readonly #http: Http;
  readonly #now: () => number;
  /** "Manter conectado" do último login nesta aba (o cookie não diz); padrão: sim. */
  #remembered = true;
  #stepUpUntil: number | null = null;
  #user: ServerUser | null = null;

  constructor(o: ApiAuthOptions = {}) {
    this.#http = createHttp(o);
    this.#now = o.now ?? Date.now;
  }

  #session(u: ServerUser): SessionInfo {
    this.#user = u;
    const now = this.#now();
    const stepUp = this.#stepUpUntil != null && this.#stepUpUntil > now ? this.#stepUpUntil : null;
    return sessionFromUser(u, { remembered: this.#remembered, stepUpUntil: stepUp, now });
  }

  async session(): Promise<SessionInfo | null> {
    const res = await this.#http.raw('/auth/session');
    if (res.status === 401) {
      this.#user = null;
      return null;
    }
    if (!res.ok) throw { code: 'source_unavailable', message: `Sessão indisponível (${res.status}).` };
    const body = (await res.json()) as { user?: ServerUser };
    return body.user ? this.#session(body.user) : null;
  }

  async signIn(i: { email: string; password: string; remember: boolean }): Promise<SignInResult> {
    const res = await this.#http.raw('/auth/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: i.email.trim(), password: i.password, remember: i.remember }),
    });
    if (res.status === 401 || res.status === 400) return { status: 'invalid' };
    if (res.status === 429) return { status: 'throttled', retryAt: this.#now() + THROTTLE_MS };
    if (!res.ok) throw { code: 'source_unavailable', message: `Login indisponível (${res.status}).` };
    const body = (await res.json()) as { user: ServerUser };
    this.#remembered = i.remember;
    this.#stepUpUntil = null;
    return { status: 'ok', session: this.#session(body.user) };
  }

  verifyTotp(_challengeId: Id, _code: string): Promise<SessionInfo> {
    return unavailable('O código de 2 etapas');
  }

  /** Confirma a identidade com a senha (o próprio login a confere); vale 15 min. */
  async stepUp(i: { password?: string; totp?: string }): Promise<SessionInfo> {
    if (!i.password) return unavailable('O código de 2 etapas');
    const email = this.#user?.email ?? (await this.session())?.email;
    if (!email) throw { code: 'forbidden', message: 'Sua sessão terminou. Entre de novo.' };
    const r = await this.signIn({ email, password: i.password, remember: this.#remembered });
    if (r.status === 'throttled')
      throw { code: 'rate_limited', message: 'Muitas tentativas. Espere alguns minutos.' };
    if (r.status !== 'ok') throw { code: 'invalid', message: 'Senha incorreta.' };
    this.#stepUpUntil = this.#now() + STEP_UP_MS;
    return this.#session(this.#user ?? { email, name: r.session.viewer.name, role: 'owner' });
  }

  async signOut(): Promise<void> {
    this.#user = null;
    this.#stepUpUntil = null;
    try {
      await this.#http.raw('/auth/logout', { method: 'POST' });
    } catch {
      // sem rede: o cookie vence sozinho; a interface já sai
    }
  }

  /** Troca a própria senha (Conta › Segurança). O servidor derruba as outras sessões desse login. */
  async changePassword(current: string, next: string): Promise<void> {
    const res = await this.#http.raw('/auth/password', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ current, next }),
    });
    if (res.ok) return;
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw {
      code: res.status === 401 ? 'forbidden' : 'invalid',
      message: body?.error ?? `Não foi possível trocar a senha (${res.status}).`,
    };
  }

  /** Sem e-mail de redefinição no servidor: a tela mostra o mesmo aviso de sempre. */
  requestPasswordReset(_email: string): Promise<void> {
    return unavailable('A redefinição de senha por e-mail');
  }

  resetPassword(_token: string, _password: string): Promise<void> {
    return unavailable('A redefinição de senha por e-mail');
  }

  /** Aceita o convite do link: define a senha e já entra (a sessão vem no cookie). */
  async acceptInvite(token: string, password: string, name?: string): Promise<SessionInfo> {
    const res = await this.#http.raw('/auth/invite/accept', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token, password, ...(name ? { name } : {}) }),
    });
    const body = (await res.json().catch(() => null)) as { user?: ServerUser; error?: string } | null;
    if (!res.ok || !body?.user)
      throw {
        code: res.status === 429 ? 'rate_limited' : 'invalid',
        message: body?.error ?? 'Este convite não vale mais. Peça um novo ao dono.',
      };
    this.#remembered = true;
    this.#stepUpUntil = null;
    return this.#session(body.user);
  }

  /** Equipe (só o dono; o servidor confere): os três níveis que o painel aplica. */
  readonly team = {
    list: async (): Promise<TeamMember[]> => {
      const r = await this.#http.get<{ members: ServerMember[]; invites: ServerInvite[] }>('/team');
      return [...r.members.map(toMember), ...r.invites.map(inviteMember)];
    },
    invite: async (i: { email: string; name: string; role: Role }): Promise<InviteResult> => {
      const r = await this.#http.post<{ id: string; link: string; expiresAt: number; emailed?: boolean }>(
        '/team/invite',
        {
          email: i.email.trim(),
          name: i.name.trim(),
          role: i.role,
        },
      );
      return {
        memberId: inviteId(r.id),
        link: r.link,
        expiresAt: r.expiresAt,
        ...(r.emailed ? { emailed: true } : {}),
      };
    },
    update: async (id: Id, patch: { role?: Role; status?: 'active' | 'disabled' }): Promise<TeamMember> => {
      const r = await this.#http.post<{ member: ServerMember }>('/team/member', {
        email: emailOf(id),
        ...(patch.role ? { role: patch.role } : {}),
        ...(patch.status ? { status: patch.status } : {}),
      });
      return toMember(r.member);
    },
    revokeSessions: async (id: Id): Promise<void> => {
      await this.#http.post('/team/revoke', { email: emailOf(id) });
    },
    audit: async (q: { cursor?: string; limit: number }): Promise<Page<SuiteAuditEntry>> => {
      const r = await this.#http.get<{ rows: ServerAudit[]; next: number | null }>('/team/audit', {
        limit: q.limit,
        before: q.cursor ?? null,
      });
      return {
        rows: r.rows.map((x) => ({
          id: String(x.id),
          at: x.at,
          actorId:
            x.type === 'panel_user_set' ? 'system' : memberId(x.detail.by ?? x.detail.email ?? 'sistema'),
          action: AUDIT_ACTION[x.type] ?? 'settings',
          area: 'suite',
          detail: auditDetail(x),
        })),
        nextCursor: r.next != null ? String(r.next) : null,
        total: r.rows.length,
      };
    },
    pendingInvites: async () => {
      const r = await this.#http.get<{ invites: ServerInvite[] }>('/team/invites');
      return r.invites.map((i) => ({
        memberId: inviteId(i.id),
        email: i.email,
        name: i.name,
        role: i.role as Role,
        createdAt: i.createdAt,
        expiresAt: i.expiresAt,
        createdBy: memberId(i.createdBy),
      }));
    },
    cancelInvite: async (id: Id): Promise<void> => {
      await this.#http.post('/team/invites/cancel', { id: id.replace(/^inv_/, '') });
    },
  };
}
